import React, { useState } from "react";
import { getStates } from "../../services/helpers";
import ToolTip from "../component-tools/ToolTip";
import MonthYearPicker from "../component-tools/MonthYearPicker";
import ConfirmPopup from "../client-components/ConfirmPopup";

export interface EmployerCongregation {
  employerName: string;
  ecContactPerson: string;
  ecConfidentialName: boolean;
  ecEmail: string;
  ecPhone: string;
  ecAddress: string;
  ecCity: string;
  ecState: string;
  ecZipCode: string;
  yourPosition: string;
  ecFromDate: Date;
  ecToDate: Date;
  ecCurrent: boolean;
  ecMailingAddressDifferent: boolean;
  ecMailingAddress: string;
  ecMailingCity: string;
  ecMailingState: string;
  ecMailingZipCode: string;
}

interface EmployerCongregationFormProps {
  employerCongregation: EmployerCongregation;
  validationErrors: { [key: string]: string };
  onInputChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index: number,
  ) => void;
  onDelete: () => void;
  index: number;
}

const EmployerCongregationForm: React.FC<EmployerCongregationFormProps> = ({
  employerCongregation,
  validationErrors,
  onInputChange,
  onDelete,
  index,
}) => {
  const states = getStates();
  const [
    showConfirmationConfidentialContact,
    setShowConfirmationConfidentialContact,
  ] = useState(false);
  return (
    <div className="data-container">
      <div className="text-sub-sub-header" style={{ flex: "0 0 100%" }}>
        Employer/Substantial Service #{index + 1}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 40%" }}>
        <label className="field-label">
          Name of Employer/Substantial Service:
        </label>
        <input
          className={`form-control ${
            validationErrors[`employerName-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.employerName || ""}
          onChange={(e) => onInputChange(e, "employerName", index)}
        />
        {validationErrors[`employerName-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`employerName-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Contact Person:</label>
        <input
          className={`form-control ${
            validationErrors[`ecContactPerson-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.ecContactPerson || ""}
          onChange={(e) => onInputChange(e, "ecContactPerson", index)}
        />
        {validationErrors[`ecContactPerson-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecContactPerson-${index}`]}
          </div>
        )}
      </div>

      <div className="checkbox-long">
        <div className="field-input" style={{ flex: "0 0 3%" }}>
          <input
            className="form-check-input"
            type="checkbox"
            checked={employerCongregation.ecConfidentialName}
            onChange={(e) => {
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "ecConfidentialName",
                index,
              );
              setShowConfirmationConfidentialContact(true);
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "5px" }}>
          <label className="field-label">Confidential Contact</label>
          <ToolTip
            tooltipText="Check here to delay contact with this entity, noting this may slow file processing while we confirm with client."
            placement="top"
            className="info-icon-spec"
          ></ToolTip>
        </div>
        <div></div>
      </div>
      {showConfirmationConfidentialContact &&
        employerCongregation.ecConfidentialName && (
          <ConfirmPopup
            onConfirm={() => {
              setShowConfirmationConfidentialContact(false);
            }}
            onCancel={() => setShowConfirmationConfidentialContact(false)}
            message="By checking this box, you are requesting that we do not contact this entity. You understand this may delay your background check while we confirm approval from the client to move forward without that contact."
          />
        )}

      <div className="field-stacked" style={{ flex: "0 0 40%" }}>
        <label className="field-label">Email:</label>
        <input
          className={`form-control ${
            validationErrors[`ecEmail-${index}`] ? "is-invalid" : ""
          }`}
          type="email"
          value={employerCongregation.ecEmail || ""}
          onChange={(e) => onInputChange(e, "ecEmail", index)}
        />
        {validationErrors[`ecEmail-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecEmail-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Phone:</label>
        <input
          className={`form-control ${
            validationErrors[`ecPhone-${index}`] ? "is-invalid" : ""
          }`}
          type="tel"
          value={employerCongregation.ecPhone || ""}
          onChange={(e) => onInputChange(e, "ecPhone", index)}
        />
        {validationErrors[`ecPhone-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecPhone-${index}`]}
          </div>
        )}
      </div>
      <div className="field-stacked" style={{ flex: "0 0 20%" }}></div>

      <div className="field-stacked" style={{ flex: "0 0 30%" }}>
        <label className="field-label">Address:</label>
        <input
          className={`form-control ${
            validationErrors[`ecAddress-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.ecAddress || ""}
          onChange={(e) => onInputChange(e, "ecAddress", index)}
        />
        {validationErrors[`ecAddress-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecAddress-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">City:</label>
        <input
          className={`form-control ${
            validationErrors[`ecCity-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.ecCity || ""}
          onChange={(e) => onInputChange(e, "ecCity", index)}
        />
        {validationErrors[`ecCity-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecCity-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">State:</label>
        <select
          className={`form-select ${
            validationErrors[`ecState-${index}`] ? "is-invalid" : ""
          }`}
          value={employerCongregation.ecState || ""}
          onChange={(e) => onInputChange(e, "ecState", index)}
        >
          <option value="">Select State</option>
          {states.map((state) => (
            <option key={state.abbreviation} value={state.abbreviation}>
              {state.name} ({state.abbreviation})
            </option>
          ))}
        </select>
        {validationErrors[`ecState-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecState-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Zip Code:</label>
        <input
          className={`form-control ${
            validationErrors[`ecZipCode-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.ecZipCode || ""}
          onChange={(e) => onInputChange(e, "ecZipCode", index)}
        />
        {validationErrors[`ecZipCode-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecZipCode-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 100%" }}>
        <label className="field-label">Your Position or Title:</label>
        <input
          className={`form-control ${
            validationErrors[`yourPosition-${index}`] ? "is-invalid" : ""
          }`}
          type="text"
          value={employerCongregation.yourPosition || ""}
          onChange={(e) => onInputChange(e, "yourPosition", index)}
          maxLength={250}
        />
        <div
          style={{
            fontSize: "14px",
            marginTop: "4px",
            textAlign: "right",
          }}
        >
          {employerCongregation.yourPosition.length ?? 0}/250
        </div>
        {validationErrors[`yourPosition-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`yourPosition-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">From Date:</label>
        <MonthYearPicker
          value={employerCongregation.ecFromDate}
          onChange={(newDate) =>
            onInputChange(
              { target: { value: newDate } } as any,
              "ecFromDate",
              index,
            )
          }
        />
        {validationErrors[`ecFromDate-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecFromDate-${index}`]}
          </div>
        )}
      </div>

      <div className="field-stacked" style={{ flex: "0 0 20%" }}>
        <label className="field-label">To Date:</label>
        <MonthYearPicker
          value={employerCongregation.ecToDate}
          onChange={(newDate) =>
            onInputChange(
              { target: { value: newDate } } as any,
              "ecToDate",
              index,
            )
          }
        />
        {validationErrors[`ecToDate-${index}`] && (
          <div className="invalid-feedback">
            {validationErrors[`ecToDate-${index}`]}
          </div>
        )}
      </div>

      <div className="field-long" style={{ flex: "0 0 20%" }}>
        <label className="field-label">Is Current:</label>
        <div>
          <input
            className="form-check-input"
            type="checkbox"
            checked={employerCongregation.ecCurrent}
            onChange={(e) =>
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "ecCurrent",
                index,
              )
            }
          />
        </div>
      </div>

      <div className="field-long" style={{ flex: "0 0 100%" }}>
        <div>
          <input
            className="form-check-input"
            type="checkbox"
            checked={employerCongregation.ecMailingAddressDifferent}
            onChange={(e) =>
              onInputChange(
                e as React.ChangeEvent<HTMLInputElement>,
                "ecMailingAddressDifferent",
                index,
              )
            }
          />
        </div>
        <label className="field-label" style={{ textWrap: "wrap" }}>
          Mailing Address Different than Physical Address
        </label>
      </div>

      {employerCongregation.ecMailingAddressDifferent && (
        <>
          <div className="field-stacked" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Mailing Address:</label>
            <input
              className={`form-control ${
                validationErrors[`ecMailingAddress-${index}`]
                  ? "is-invalid"
                  : ""
              }`}
              type="text"
              value={employerCongregation.ecMailingAddress || ""}
              onChange={(e) => onInputChange(e, "ecMailingAddress", index)}
            />
            {validationErrors[`ecMailingAddress-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`ecMailingAddress-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing City:</label>
            <input
              className={`form-control ${
                validationErrors[`ecMailingCity-${index}`] ? "is-invalid" : ""
              }`}
              type="text"
              value={employerCongregation.ecMailingCity || ""}
              onChange={(e) => onInputChange(e, "ecMailingCity", index)}
            />
            {validationErrors[`ecMailingCity-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`ecMailingCity-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing State:</label>
            <select
              className={`form-select ${
                validationErrors[`ecMailingState-${index}`] ? "is-invalid" : ""
              }`}
              value={employerCongregation.ecMailingState || ""}
              onChange={(e) => onInputChange(e, "ecMailingState", index)}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.abbreviation} value={state.abbreviation}>
                  {state.name} ({state.abbreviation})
                </option>
              ))}
            </select>
            {validationErrors[`ecMailingState-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`ecMailingState-${index}`]}
              </div>
            )}
          </div>

          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Mailing Zip Code:</label>
            <input
              className={`form-control ${
                validationErrors[`ecMailingZipCode-${index}`]
                  ? "is-invalid"
                  : ""
              }`}
              type="text"
              value={employerCongregation.ecMailingZipCode || ""}
              onChange={(e) => onInputChange(e, "ecMailingZipCode", index)}
            />
            {validationErrors[`ecMailingZipCode-${index}`] && (
              <div className="invalid-feedback">
                {validationErrors[`ecMailingZipCode-${index}`]}
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ flex: "0 0 100%" }}>
        <button className="oxbtn btn-red" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
};

export default EmployerCongregationForm;
